import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { get } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { getFullUrl } from '../config/apiConfig';
import { 
  Search,
  Filter,
  Clock,
  BookOpen,
  Play,
  ChevronDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import logger from '../utils/logger';

const ExamList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshToken } = useAuth();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedGrade, setSelectedGrade] = useState('All');
  const [selectedSet, setSelectedSet] = useState('All');
  const [showFilters, setShowFilters] = useState(true);
  const [currentCarouselPage, setCurrentCarouselPage] = useState(0);
  const examsPerPage = 6;

  // Read grade parameter from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const gradeParam = urlParams.get('grade');
    if (gradeParam) {
      setSelectedGrade(gradeParam);
      logger.log(`Set initial grade filter from URL: ${gradeParam}`);
    }
  }, [location.search]);

  // Fetch exams from API
  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true);
        const response = await get(getFullUrl('exams'));
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        logger.log('Fetched exams:', data);
        
        // Parse the response body
        let actualData = data;
        if (data && data.body) {
          actualData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        }
        
        // Handle the new backend structure where examSets is the main array
        const examsArray = [];
        
        if (actualData && actualData.examSets && Array.isArray(actualData.examSets)) {
          actualData.examSets.forEach(set => {
            // Each set object is now an exam directly (not nested under set.exams)
            const examObj = {
              id: set.examId,
              title: set.type || 'Exam',
              description: `Grade ${set.PK ? set.PK.split('#')[1] : 'Unknown'} - ${set.title}`,
              category: set.type || 'General',
              difficulty: 'Medium', // Default difficulty since it's not in the new structure
              duration: set.duration || 40,
              questions: set.questionCount, // Questions count not available in new structure
              tags: set.tags || [],
              grade: set.PK ? set.PK.split('#')[1] : 'Unknown',
              package: set.PK ? set.PK.split('#')[3] : 'PKG_STANDARD',
              setNumber: set.setNumber,
              setInfo: set.title,
              purchasedPackage: set.PK ? set.PK.split('#')[3] : 'PKG_STANDARD',
              isCompleted: set.isCompleted || false
            };
            examsArray.push(examObj);
          });
        }
        
        logger.log('Parsed exams array:', examsArray);
        setExams(examsArray);
        
      } catch (err) {
        logger.error('Error fetching exams:', err);
        
        // Check for specific "Access denied" error
        if (err.message && err.message.includes('Access denied')) {
          setError('You have not purchased this package. Please purchase a package to access these exams.');
        }
        // Handle 401 Unauthorized error by refreshing token
        else if (err.message && err.message.includes('401')) {
          logger.log('🔄 401 error detected, attempting token refresh...');
          try {
            await refreshToken();
            logger.log('✅ Token refreshed successfully, retrying request...');
            
            // Retry the request after token refresh
            // const retryResponse = await get('https://riaqg7zqtj.execute-api.ap-southeast-2.amazonaws.com/dev/exams');
            const retryResponse = await get(getFullUrl('exams'))
            if (!retryResponse.ok) {
              throw new Error(`HTTP error! status: ${retryResponse.status}`);
            }
            
            const retryData = await retryResponse.json();
            logger.log('Fetched exams after token refresh:', retryData);
            
            // Parse the response body
            let actualData = retryData;
            if (retryData && retryData.body) {
              actualData = typeof retryData.body === 'string' ? JSON.parse(retryData.body) : retryData.body;
            }
            
            // Handle the new backend structure where examSets is the main array
            const examsArray = [];
            
            if (actualData && actualData.examSets && Array.isArray(actualData.examSets)) {
              actualData.examSets.forEach(set => {
                // Each set object is now an exam directly (not nested under set.exams)
                const examObj = {
                  id: set.examId,
                  title: set.type || 'Exam',
                  description: `Grade ${set.PK ? set.PK.split('#')[1] : 'Unknown'} - ${set.title}`,
                  category: set.type || 'General',
                  difficulty: 'Medium', // Default difficulty since it's not in the new structure
                  duration: set.duration || 40,
                  questions: set.questionCount, // Questions count not available in new structure
                  tags: set.tags || [],
                  grade: set.PK ? set.PK.split('#')[1] : 'Unknown',
                  package: set.PK ? set.PK.split('#')[3] : 'PKG_STANDARD',
                  setNumber: set.setNumber,
                  setInfo: set.title,
                  purchasedPackage: set.PK ? set.PK.split('#')[3] : 'PKG_STANDARD',
                  isCompleted: set.isCompleted || false
                };
                examsArray.push(examObj);
              });
            }
            
            logger.log('Parsed exams array after token refresh:', examsArray);
            setExams(examsArray);
            
          } catch (refreshError) {
            logger.error('❌ Token refresh failed:', refreshError);
            setError('Session expired. Please log in again.');
            // Redirect to login after a short delay
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          }
        } else {
          setError('Failed to load exams. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, [navigate, refreshToken]);

  // const categories = ['All', ...new Set(exams.map(exam => exam.category))];
  const categories = ['All', 'NUMERACY','READING','WRITING','LANGUAGE'];
  const grades = ['All', '3', '5', '7', '9'];
  const sets = ['All', ...new Set(exams.map(exam => exam.setNumber).filter(Boolean).sort((a, b) => a - b))];

  const filteredExams = exams.filter(exam => {
    const matchesSearch = exam.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         exam.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || exam.category === selectedCategory;
    const matchesGrade = selectedGrade === 'All' || exam.grade === selectedGrade;
    const matchesSet = selectedSet === 'All' || Number(exam.setNumber) === selectedSet;
    
    return matchesSearch && matchesCategory && matchesGrade && matchesSet;
  });

  // Carousel pagination
  const totalPages = Math.ceil(filteredExams.length / examsPerPage);
  const startIndex = currentCarouselPage * examsPerPage;
  const endIndex = startIndex + examsPerPage;
  const currentExams = filteredExams.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentCarouselPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentCarouselPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  // Reset carousel page when filters change
  useEffect(() => {
    setCurrentCarouselPage(0);
  }, [searchTerm, selectedCategory, selectedGrade, selectedSet]);

  const getTagColor = (tag) => {
    // Dynamic tag colors based on tag content
    const colorMap = {
      'BASIC': 'bg-blue-600 text-white',
      'STANDARD': 'bg-purple-600 text-white',
      'PREMIUM': 'bg-yellow-500 text-white',
      'READING': 'bg-orange-200 text-orange-800',
      'WRITING': 'bg-lime-400 text-lime-900',
      'NUMERACY': 'bg-amber-200 text-amber-800',
      'LANGUAGE': 'bg-amber-500 text-white',
      'Grade 3': 'bg-blue-100 text-blue-800',
      'Grade 5': 'bg-green-100 text-green-800',
      'Grade 7': 'bg-yellow-100 text-yellow-800',
      'Grade 9': 'bg-red-100 text-red-800',
      'Mathematics': 'bg-purple-100 text-purple-800',
      'Science': 'bg-indigo-100 text-indigo-800',
      'Language': 'bg-orange-100 text-orange-800'
    };
    
    return colorMap[tag] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Practice Exams</h1>
        <p className="mt-2 text-gray-600">
          Choose from our collection of NAPLAN-style practice tests
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exams...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-12">
          {error.includes('You have not purchased this package') ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 max-w-lg mx-auto">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 2.502-3.178V7.825c0-1.51-.962-2.5-2.178-2.5H5.414C3.904 5.325 3 6.325 3 7.825v6.997c0 1.511.904 2.5 2.178 2.5h13.856c1.274 0 2.178-.989 2.178-2.178V7.825c0-1.189-.904-2.178-2.178-2.178zm-6.938 4.012a1.125 1.125 0 110 2.25 0 1.125 1.125 0 01-2.25 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">Premium Access Required</h3>
                  <p className="text-amber-700 mb-4">You have not purchased this package. To access these practice exams, please purchase a package that suits your needs.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link 
                      to="/packages" 
                      className="bg-amber-600 text-white px-6 py-2 rounded-lg hover:bg-amber-700 transition-colors font-medium"
                    >
                      View Packages
                    </Link>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="bg-white text-amber-600 border border-amber-600 px-6 py-2 rounded-lg hover:bg-amber-50 transition-colors font-medium"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-800">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* Exam Content */}
      {!loading && !error && (
        <>
          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-4">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
                    <input
                      type="text"
                      placeholder="Search exams..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Filter className="h-4 w-4" />
                  <span>Filters</span>
                  <ChevronDown className={`h-4 w-4 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Filter Options */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="input-field"
                      >
                        {categories.map(category => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grade
                      </label>
                      <select
                        value={selectedGrade}
                        onChange={(e) => setSelectedGrade(e.target.value)}
                        className="input-field"
                      >
                        {grades.map(grade => (
                          <option key={grade} value={grade}>
                            {grade === 'All' ? 'All' : `Grade ${grade}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Set
                      </label>
                      <select
                        value={selectedSet}
                        onChange={(e) => setSelectedSet(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                        className="input-field"
                      >
                        {sets.map(set => (
                          <option key={set} value={set}>
                            {set === 'All' ? 'All' : `Set ${set}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Exam Carousel */}
          {currentExams.length > 0 ? (
            <div className="relative">
              {/* Carousel Navigation */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentCarouselPage === 0}
                  className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-600" />
                </button>
                
                <div className="text-center">
                  <span className="text-sm text-gray-600">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredExams.length)} of {filteredExams.length} exams
                  </span>
                  {totalPages > 1 && (
                    <div className="flex justify-center space-x-1 mt-2">
                      {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentCarouselPage(i)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            i === currentCarouselPage ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={handleNextPage}
                  disabled={currentCarouselPage === totalPages - 1}
                  className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              {/* Exam Grid - Maximum 6 cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentExams.map(exam => (
              <div key={exam.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{exam.title}</h3>
                    <div className="flex flex-wrap gap-1">
                      {exam.tags && exam.tags.map((tag, index) => (
                        <span key={index} className={`px-2 py-1 text-xs font-medium rounded-full ${getTagColor(tag)}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{exam.description}</p>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <Clock className="h-4 w-4 mr-1" />
                    {exam.duration} minutes
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <BookOpen className="h-4 w-4 mr-1" />
                    {exam.questions} questions
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{exam.category}</span>
                    <button
                      onClick={async () => {
                        try {
                          // Navigate to exam participation with exam data (questions will be fetched there)
                            navigate(`/exam/${exam.id}`, { 
                              state: { 
                                examData: exam
                              } 
                            });
                        } catch (err) {
                          logger.error('Error starting exam:', err);
                          alert('Failed to start exam. Please try again.');
                        }
                      }}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                        exam.isCompleted === true 
                          ? 'bg-red-500 text-white border-red-500 hover:bg-red-600' 
                          : 'btn-primary'
                      }`}
                    >
                      <Play className="h-4 w-4" />
                      <span>{exam.isCompleted ? 'Retry Exam' : 'Start Exam'}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
              </div>
            </div>
          ) : (
            /* No Results */
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No exams found</h3>
              <p className="text-gray-600">
                Try adjusting your search or filters to find what you're looking for.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ExamList;
